import { OktaAuth } from '@okta/okta-auth-js';
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { GlanceParams, UnparsedDefaultFilters, DataStatus } from '../../types';
/* eslint-disable no-async-promise-executor */
import { fetchCall } from '../../services/api';
import { store } from '../../redux/store';

import {
  GlanceSummarySales,
  GlanceSummaryMedia,
  Segment,
  GraphTable,
  GraphTableData,
  GraphTableDataObject
} from '../../types';
import { tokenHasExpired } from '../../helpers/auth';

export async function fetchGlanceSummaryData(
  endpoint: string,
  params: GlanceParams,
  title: string,
  oktaAuth?: OktaAuth
): Promise<GlanceSummarySales> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchCall(`${api}${endpoint}`, params);
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(response);
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401 && oktaAuth)
        tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject(`Error fetching ${title || 'some'} data. Please try again.`);
    }
  });
}

export async function fetchGlanceGridData(endpoint: string, params: GlanceParams, title: string): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}${endpoint}`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching ${title || 'some'} data. Please try again.`);
    }
  });
}

export async function fetchGlanceDates(component: string, params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/client/metadata/available/${component}`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching available dates data. Please try again.`);
    }
  });
}

export async function fetchGlanceData(component: string, params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/${component}`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching available dates data. Please try again.`);
    }
  });
}

export function getDefaultParams(options: UnparsedDefaultFilters) {
  if (options) {
    const params = {
      interval: 'weekly',
      retailerIds: options.retailers?.map((retailer) => retailer.id).toString(),
      campaignTypeIds: options.retailers?.map((retailer) => retailer.campaignTypes).toString(),
      sellerTypes: options.sellerTypes?.toString()
    };

    return params;
  } else {
    return {};
  }
}

export async function fetchGrid(endpoint: string, params: unknown, title: string): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}${endpoint}`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching ${title || 'some'} data. Please try again.`);
    }
  });
}

export async function fetchGlanceRetailers(params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/filter/atglance/retailer`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching retailers. Please try again.`);
    }
  });
}

export async function fetchGlanceMarkets(params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/filter/fila/markets`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching markets. Please try again.`);
    }
  });
}

export async function fetchGlanceAdPlatforms(params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/filter/adplatform?`, params));
    } catch (error) {
      console.error(error);
      reject(`Error fetching ad platforms. Please try again.`);
    }
  });
}

export async function fetchSpgr(params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;

  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/summary/media/spgr`, params));
    } catch (error) {
      console.error(error);
      reject('Error fetching SPGR data. Please try again.');
    }
  });
}

export async function fetchSpgrGrid(params: unknown): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;

  return new Promise(async (resolve, reject) => {
    try {
      resolve(await fetchCall(`${api}/atglance/grid/media/spgr`, params));
    } catch (error) {
      console.error(error);
      reject('Error fetching SPGR grid data. Please try again.');
    }
  });
}

// ____  Segments
export const returnSummarySegments = (id: string, data: any): (Segment | null)[] => {
  if (id === 'glanceSales') {
    return generateSummarySalesSegment(data);
  }
  if (id === 'glanceMedia') {
    return generateSummaryMediaSegment(data?.glanceViewSummary);
  }
  return [];
};

export const generateSummarySalesSegment = (data: GlanceSummarySales): Segment[] => {
  const { orderedRevenue, glanceViews, conversionRate }: GlanceSummarySales = data;
  return [
    {
      title: 'Ordered Revenue',
      currencyCode: orderedRevenue?.currencyCode,
      currentValue: orderedRevenue?.currentValue,
      currentValueType: orderedRevenue?.currentValueType,
      wowPercentage: orderedRevenue?.wowPercentage,
      index: 0,
      displayData: ['WoW'],
      dataStatus: orderedRevenue?.dataStatus
    },
    {
      title: 'Glance Views',
      currencyCode: glanceViews?.currencyCode,
      currentValue: glanceViews?.currentValue,
      currentValueType: glanceViews?.currentValueType,
      wowPercentage: glanceViews?.wowPercentage,
      index: 1,
      displayData: ['WoW'],
      dataStatus: glanceViews?.dataStatus
    },
    {
      title: 'Total Conversion Rate',
      currencyCode: conversionRate?.currencyCode,
      currentValue: conversionRate?.currentValue,
      currentValueType: conversionRate?.currentValueType,
      wowPercentage: conversionRate?.wowPercentage,
      index: 2,
      displayData: ['WoW'],
      dataStatus: conversionRate?.dataStatus
    }
  ];
};

export const generateSummaryMediaSegment = (
  data: GlanceSummaryMedia,
  client = '',
  isAmazon = true
): (Segment | null)[] => {
  if (!data) return [];

  const { clicks, conversions, sales, spend, cpc, cpa, spgr }: GlanceSummaryMedia = data;
  let [
    salesSegment,
    cpcSegment,
    spendSegment,
    cpaSegment,
    spgrSegment,
    clicksSegment,
    conversionsSegment
  ]: (Segment | null)[] = [null, null, null, null, null, null, null];
  const spgrDataStatus: DataStatus | undefined = {
    status: 'PARTIAL',
    label: '6/13',
    message: 'Calculation is using 6 of 13 markets: IT, DE, US, BR, AE, ES'
  };

  if (sales !== undefined) {
    salesSegment = {
      title: isAmazon ? 'Sponsored Ad Sales' : 'Sales',
      currencyCode: sales?.currencyCode,
      currentValue: sales?.currentValue,
      currentValueType: sales?.currentValueType,
      wowPercentage: sales?.wowPercentage,
      index: 3,
      displayData: ['WoW'],
      dataStatus: sales?.dataStatus
    };
  }
  if (cpc !== undefined) {
    cpcSegment = {
      title: 'CPC',
      currencyCode: cpc?.currencyCode,
      currentValue: cpc?.currentValue,
      currentValueType: cpc?.currentValueType,
      wowPercentage: cpc?.wowPercentage,
      index: 4,
      displayData: ['WoW'],
      dataStatus: cpc?.dataStatus
    };
  }
  if (spend !== undefined) {
    spendSegment = {
      title: 'Spend',
      currencyCode: spend?.currencyCode,
      currentValue: spend?.currentValue,
      currentValueType: spend?.currentValueType,
      wowPercentage: spend?.wowPercentage,
      index: 5,
      displayData: ['WoW'],
      dataStatus: spend?.dataStatus
    };
  }
  if (cpa !== undefined) {
    cpaSegment = {
      title: 'CPA',
      currencyCode: cpa?.currencyCode,
      currentValue: cpa?.currentValue,
      currentValueType: cpa?.currentValueType,
      wowPercentage: cpa?.wowPercentage,
      index: 6,
      displayData: ['WoW'],
      dataStatus: cpa?.dataStatus
    };
  }
  if (clicks !== undefined) {
    clicksSegment = {
      title: isAmazon ? 'Sponsored Ad Clicks' : 'Clicks',
      currencyCode: clicks?.currencyCode,
      currentValue: clicks?.currentValue,
      currentValueType: clicks?.currentValueType,
      wowPercentage: clicks?.wowPercentage,
      index: 8,
      displayData: ['WoW'],
      dataStatus: clicks?.dataStatus
    };
  }
  if (conversions !== undefined) {
    conversionsSegment = {
      title: isAmazon ? 'Sponsored Ad Conversions' : 'Conversions',
      currencyCode: conversions?.currencyCode,
      currentValue: conversions?.currentValue,
      currentValueType: conversions?.currentValueType,
      wowPercentage: conversions?.wowPercentage,
      index: 9,
      displayData: ['WoW'],
      dataStatus: conversions?.dataStatus
    };
  }
  if (spgr !== undefined) {
    spgrSegment = {
      title: 'Spend as % of Gross Revenue',
      currencyCode: spgr?.currencyCode,
      currentValue: spgr?.currentValue,
      currentValueType: spgr?.currentValueType,
      wowPercentage: spgr?.wowPercentage,
      index: 7,
      displayData: ['WoW'],
      dataStatus: client === 'demo' ? spgrDataStatus : spgr?.dataStatus
    };
  }

  return [salesSegment, cpcSegment, spendSegment, cpaSegment, spgrSegment, clicksSegment, conversionsSegment];
};

const returnTableObject = (data: GraphTable): GraphTableData[] => {
  const values = data;
  return Object.values(values);
};

// ___ Tables
export const returnGridTable = (id: string, data: GraphTable): GraphTableDataObject[] => {
  if (id === 'glanceGridSales') {
    return [{ title: 'Glance Grid Sales', index: 0, data: returnTableObject(data) }];
  }
  if (id === 'glanceGridMedia') {
    return [{ title: 'Glance Grid Media', index: 1, data: returnTableObject(data) }];
  }
  return [];
};

// ___ Share
export const returnAmazonShareData = (data: any): Segment[] => {
  const formattedShareData = data.map((item: any) => {
    const segmentItem = {
      ...item,
      title: item.country
    };
    return segmentItem;
  });
  return formattedShareData || [];
};

// ___ Sales
export const returnSalesData = (data: any): Segment[] => {
  const formattedSalesData = {
    ...data,
    title: 'Global Net Sales'
  };
  return formattedSalesData || null;
};
